'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import {
  AuthState,
  AuthContextType,
  LoginRequest,
  RegisterRequest,
  ChangePasswordRequest,
  UserSessionResponse,
} from '../types/auth';

/**
 * Provider di autenticazione basato sulla sessione SSO.
 *
 * Sostituisce il precedente provider statico, che restituiva
 * `isAuthenticated: true` con un utente amministratore fittizio e un token
 * 'dev-token' — senza alcun controllo su NODE_ENV, quindi anche in produzione.
 *
 * Qui non esiste alcun token lato browser: l'autenticazione vive in un cookie
 * httpOnly che il browser invia da se' alle chiamate same-origin, e l'unico
 * modo per sapere chi e' l'utente e' chiederlo al server via /auth/session.
 *
 * Il nome del file e degli export resta quello storico per non toccare i ~50
 * consumatori; l'export da preferire e' `SessionAuthProvider`.
 */

const UNAUTHENTICATED: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,          // nessun token nel browser: e' il punto del BFF
  refreshToken: null,
  isLoading: true,      // si parte "in caricamento": la sessione va verificata
  error: null,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function SessionAuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>(UNAUTHENTICATED);

  const loadSession = useCallback(async (): Promise<boolean> => {
    try {
      // `credentials: 'same-origin'` e' gia' il default di fetch, esplicitato
      // qui perche' e' l'unico meccanismo che autentica la richiesta.
      const response = await fetch('/auth/session', {
        credentials: 'same-origin',
        cache: 'no-store',
      });

      if (!response.ok) {
        setAuthState({ ...UNAUTHENTICATED, isLoading: false });
        return false;
      }

      const data = (await response.json()) as UserSessionResponse;
      setAuthState({
        isAuthenticated: true,
        user: data,
        token: null,
        refreshToken: null,
        isLoading: false,
        error: null,
      });
      return true;
    } catch {
      // Rete irraggiungibile: non autenticato, ma va distinto da un 401 per
      // non far credere all'utente di essere stato disconnesso.
      setAuthState({
        ...UNAUTHENTICATED,
        isLoading: false,
        error: 'Impossibile verificare la sessione',
      });
      return false;
    }
  }, []);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  // L'accesso avviene sull'identity provider: si esce dall'applicazione e si
  // torna al punto di partenza. Nessuna credenziale passa da qui.
  const login = useCallback(async (_credentials?: LoginRequest) => {
    const returnTo = typeof window !== 'undefined' ? window.location.href : '';
    window.location.href = `/auth/login?return_to=${encodeURIComponent(returnTo)}`;
  }, []);

  const logout = useCallback(async (_logoutAll?: boolean) => {
    window.location.href = '/auth/logout';
  }, []);

  const unsupported = (operazione: string) => async () => {
    throw new Error(
      `${operazione} non è gestita dall'applicazione: l'account si amministra sul portale SSO.`
    );
  };

  const value = useMemo<AuthContextType>(() => ({
    authState,
    login,
    logout,
    refreshAuth: async () => { await loadSession(); },
    validateSession: loadSession,
    register: unsupported('La registrazione') as unknown as (u: RegisterRequest) => Promise<void>,
    changePassword: unsupported('Il cambio password') as unknown as (p: ChangePasswordRequest) => Promise<void>,
  }), [authState, login, logout, loadSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useSessionAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useSessionAuth deve essere usato dentro un SessionAuthProvider');
  }
  return context;
}

// Alias storici, mantenuti per non toccare i consumatori esistenti.
export const StaticAuthProvider = SessionAuthProvider;
export const useStaticAuth = useSessionAuth;
export const useAuth = useSessionAuth;
